import { type Permission } from '@/lib/schemas/user.schema';

// Evaluación de permisos compartida por sidebar, guards de ruta, `useCan` y el
// editor de roles. Un solo criterio para que el menú nunca muestre algo que el
// guard bloquea.
//
// Política de `permissions`:
// - `undefined` => se permite. `POST /auth/login` no trae `permissions`; las
//   llena `GET /auth/me` (ver `SessionBoot` en `Providers.tsx`) y si esa
//   llamada falla la sesión sigue sin ellas. Bloquear ahí dejaría a un admin
//   real sin menú. El back gatea cada request igual.
// - `[]` => el user no tiene permisos. Es lo que devuelve `/auth/me` para un
//   rol vacío, no un "no llegó".

// Item de un requisito: permiso literal o grupo "cualquiera de", espejo del
// `required.some(...)` del guard del back.
export type PermissionRequirement = Permission | readonly Permission[];

// Lecturas que también se cumplen con la escritura equivalente.
export const USERS_READ: readonly Permission[] = ['users:read', 'users:manage'];
export const ROLES_READ: readonly Permission[] = ['roles:read', 'roles:manage', 'users:manage'];
// Las lecturas de `/activation-rules` aceptan cualquiera de los dos.
export const ACTIVATION_RULES_READ: readonly Permission[] = [
  'activation-rules:read',
  'activation-rules:manage',
];

export function hasPermission(granted: readonly string[] | undefined, permission: string): boolean {
  if (granted === undefined) return true;
  return granted.includes(permission);
}

export function meetsRequirement(
  granted: readonly string[] | undefined,
  requirement: PermissionRequirement,
): boolean {
  if (typeof requirement === 'string') return hasPermission(granted, requirement);
  return requirement.some((p) => hasPermission(granted, p));
}

// Todos los items deben cumplirse (AND entre items, OR dentro de un grupo).
export function meetsRequirements(
  granted: readonly string[] | undefined,
  requirements: readonly PermissionRequirement[],
): boolean {
  return requirements.every((r) => meetsRequirement(granted, r));
}

// Items que faltan, en el orden del requisito. Vacío = acceso completo.
export function missingRequirements(
  granted: readonly string[] | undefined,
  requirements: readonly PermissionRequirement[],
): PermissionRequirement[] {
  return requirements.filter((r) => !meetsRequirement(granted, r));
}

// Texto legible de un item: `users:read o users:manage`.
export function formatRequirement(requirement: PermissionRequirement): string {
  if (typeof requirement === 'string') return requirement;
  return requirement.join(' o ');
}
